// src/components/screens/EventosScreen.js
import React, { useCallback, useState, useEffect } from 'react';
import { useAppContext } from '../context/AppContext';
import { initGlobalAudio } from '../utils/audioManager';
import { useSpotQueue } from '../hooks/useSpotQueue';
import AudioSelectorForEvents from '../components/shared/AudioSelectorForEvents';
import DateDiagnostic from '../components/shared/DateDiagnostic';
import { useConfirm } from '../components/shared/ConfirmDialog';
import { useToast } from '../components/shared/Toast';

const ChevronIcon = ({ size = 12, color = '#94a3b8' }) => (
  <svg width={size} height={size} viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M2 4l4 4 4-4" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

// ✅ SEM IMPORT DE DATEUTILS AQUI!

const API_BASE = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3007';

const sanitizePersistedAudio = (audio) => {
  if (!audio || typeof audio !== 'object') return null;
  const safeAudio = {
    fileName: audio.fileName || audio.audioRef || null,
    audioRef: audio.audioRef || audio.fileName || null,
    tipo: audio.tipo || null,
    voiceName: audio.voiceName || null,
    mixed: !!audio.mixed,
    previewUrl: null
  };
  return safeAudio.audioRef ? safeAudio : null;
};

// ✅ FUNÇÕES LOCAIS:
const getDataHoje = () => {
  const agora = new Date();
  const ano = agora.getFullYear();
  const mes = String(agora.getMonth() + 1).padStart(2, '0');
  const dia = String(agora.getDate()).padStart(2, '0');
  return `${ano}-${mes}-${dia}`;
};

const normalizarDataString = (dataInput) => {
  if (!dataInput) return null;
  let data = String(dataInput).trim();
  if (data.includes('T')) data = data.split('T')[0];
  if (data.includes(' ')) data = data.split(' ')[0];
  return /^\d{4}-\d{2}-\d{2}$/.test(data) ? data : null;
};

const formatarDataExibicao = (dataString) => {
  if (!dataString) return 'N/A';
  
  let data = dataString;
  if (typeof data === 'string' && data.includes('T')) {
    data = data.split('T')[0];
  }
  
  const [ano, mes, dia] = data.split('-');
  const dataObj = new Date(parseInt(ano), parseInt(mes) - 1, parseInt(dia), 12, 0, 0);
  
  return dataObj.toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  });
};

// ✅ Adicionar CSS para animação pulse
const pulseAnimation = `
  @keyframes pulse {
    0%, 100% { opacity: 1; transform: scale(1); }
    50% { opacity: 0.7; transform: scale(1.1); }
  }
`;

function EventosScreen() {
  const { spotQueue, isPlayingSpot } = useSpotQueue();
  const {
    audioGerado,
    setAudioGerado,
    spot,
    setTela
  } = useAppContext();
  

  // Recuperar audioGerado se não tiver
  useEffect(() => {
    if (!audioGerado) {
      const saved = sessionStorage.getItem('radio-ai-audio');
      if (saved) {
        try {
          const safeAudio = sanitizePersistedAudio(JSON.parse(saved));
          if (safeAudio) {
            setAudioGerado(safeAudio);
            sessionStorage.setItem('radio-ai-audio', JSON.stringify(safeAudio));
          }
        } catch {
          sessionStorage.removeItem('radio-ai-audio');
        }
      }
    }
  }, [audioGerado, setAudioGerado]);

  // Estados locais
const { confirm, ConfirmDialogPortal } = useConfirm();
const { toast, ToastContainer } = useToast();
const [eventos, setEventos] = useState([]);
const [novoEvento, setNovoEvento] = useState({    
  dataInicio: getDataHoje(),
  dataFim: getDataHoje(),
  minutoNaHora: '18',
  horasAtivas: 'todas',
  horasCustom: [],
  tipo: 'comercial'
});


// ✅ Formatar data para exibição (sem bug de timezone)

const [erro, setErro] = useState('');
const [spotTocando, setSpotTocando] = useState(null);
const [audioSelecionado, setAudioSelecionado] = useState(null);

// ✅ Estados para minimizar seções
const [secaoAgendarExpandida, setSecaoAgendarExpandida] = useState(true);
const [secaoEventosExpandida, setSecaoEventosExpandida] = useState(true);

// Presets de horários
  const presetsHorarios = {
    todas: {
    nome: '24h (Todas)',
    horas: null
  },
  comercial: {
    nome: 'Comercial (6h-22h)',
    horas: [6,7,8,9,10,11,12,13,14,15,16,17,18,19,20,21,22]
  },
  manha: {
    nome: 'Manhã (6h-12h)',
    horas: [6,7,8,9,10,11,12]
  },
  tarde: {
    nome: 'Tarde (12h-18h)',
    horas: [12,13,14,15,16,17,18]
  },
  noite: {
    nome: 'Noite (18h-23h)',
    horas: [18,19,20,21,22,23]
  }
};

  // ✅ NOVO: Calcular minutos ocupados por eventos
  const minutosComEventos = React.useMemo(() => {
  const minutos = {};
  
  // ✅ Filtrar apenas eventos válidos (não expirados)
  const eventosValidos = eventos.filter(evento => {
    if (!evento || !evento.id) return false;
    return true;
  });
  
  eventosValidos.forEach(evento => {
    const minuto = evento.minutoNaHora;
    if (!minutos[minuto]) {
      minutos[minuto] = { ativos: 0, inativos: 0, nomes: [] };
    }
    if (evento.ativo) {
      minutos[minuto].ativos++;
    } else {
      minutos[minuto].inativos++;
    }
    minutos[minuto].nomes.push(evento.nome);
  });
  
  return minutos;
}, [eventos, eventos.length]); // ✅ Adicionar eventos.length como dependência




  // Carregar eventos
  const carregarEventos = useCallback(async () => {
    try {
      // ✅ Filtro de expirados feito no servidor (?validos=true)
      // O backend usa sua própria hora — sem risco de divergência com o cliente
      const response = await fetch(`${API_BASE}/api/eventos?validos=true`);
      const data = await response.json().catch(() => null);

      if (!response.ok) {
        throw new Error(data?.error || `HTTP ${response.status}`);
      }
      if (!Array.isArray(data)) {
        throw new Error('Resposta inválida ao carregar eventos');
      }

      console.log(`📥 ${data.length} eventos válidos carregados`);
      setEventos(data);
      return data;
    } catch (error) {
      console.error('Erro carregar eventos:', error);
      return [];
    }
  }, []);

useEffect(() => {
  carregarEventos();
}, [carregarEventos]);

// ✅ Verificar e remover eventos "fantasma" a cada 2 minutos
useEffect(() => {
  const verificarEventosFantasma = async () => {
    try {
      // Buscar lista atualizada do servidor
      const response = await fetch(`${API_BASE}/api/eventos`);
      const eventosServidor = await response.json().catch(() => null);
      if (!response.ok) {
        throw new Error(eventosServidor?.error || `HTTP ${response.status}`);
      }
      if (!Array.isArray(eventosServidor)) {
        throw new Error('Resposta inválida ao verificar eventos');
      }
      
      // IDs que existem no servidor
      const idsServidor = new Set(eventosServidor.map(e => e.id));
      
      setEventos(prevEventos => {
        const eventosFantasma = prevEventos.filter(e => !idsServidor.has(e.id));
        if (eventosFantasma.length === 0) return prevEventos;

        console.log('👻 Eventos fantasma detectados:',
          eventosFantasma.map(e => `${e.nome} (ID: ${e.id})`).join(', ')
        );

        const eventosAtualizados = prevEventos.filter(e => idsServidor.has(e.id));
        console.log(`🧹 ${eventosFantasma.length} evento(s) fantasma removido(s)`);
        return eventosAtualizados;
      });
    } catch (error) {
      console.error('Erro na verificação de fantasmas:', error);
    }
  };
  
  // Verificar a cada 2 minutos
  const intervalo = setInterval(verificarEventosFantasma, 120000);
  
  return () => clearInterval(intervalo);
}, []);

   
  useEffect(() => {
  // ✅ Forçar re-render quando eventos mudarem
  // Isso garante que a UI de minutosComEventos seja atualizada
  console.log(`📊 Eventos atualizados: ${eventos.length} eventos ativos`);
   }, [eventos]);


  useEffect(() => {
  const verificarExpirados = () => {
    const hoje = getDataHoje();
    const eventosExpirados = eventos.filter(evento => {
      const dataFim = normalizarDataString(evento.dataFim);
      return dataFim && dataFim < hoje;
    });
    
    if (eventosExpirados.length > 0) {
      console.log('⏰ Eventos expirados detectados:', 
        eventosExpirados.map(e => e.nome).join(', ')
      );
      
      // Recarregar do servidor para sincronizar
      carregarEventos();
    }
  };

  
  
  // Verificar imediatamente
  verificarExpirados();
  
  // Verificar a cada 1 minuto
  const intervalo = setInterval(verificarExpirados, 60000);
  
  return () => clearInterval(intervalo);
}, [eventos, carregarEventos]);


  // ✅ Sincronização automática a cada 30 segundos
useEffect(() => {
  const intervalo = setInterval(carregarEventos, 30000); // 30 segundos

  return () => clearInterval(intervalo);
}, [carregarEventos]);

  // Toggle hora customizada
  const toggleHoraCustom = (hora) => {
    setNovoEvento(prev => {
      const novo = [...prev.horasCustom];
      const index = novo.indexOf(hora);
      if (index > -1) {
        novo.splice(index, 1);
      } else {
        novo.push(hora);
      }
      return { ...prev, horasCustom: novo };
    });
  };

  // FUNÇÃO ADICIONAR EVENTO
  const adicionarEvento = async () => {
    if (!novoEvento.dataInicio || !novoEvento.dataFim) {
      setErro('Preencha todos os campos obrigatórios');
      return;
    }

    if (!audioSelecionado) {
      setErro('Selecione um áudio para o evento');
      return;
    }

    const minuto = parseInt(novoEvento.minutoNaHora);
    if (minuto < 0 || minuto > 59) {
      setErro('Minuto deve estar entre 0 e 59');
      return;
    }

    // Determinar horas ativas
    let horasAtivas = null;
    if (novoEvento.horasAtivas === 'custom') {
      horasAtivas = novoEvento.horasCustom.sort((a,b) => a-b);
      if (horasAtivas.length === 0) {
        setErro('Selecione pelo menos 1 hora');
        return;
      }
    } else if (novoEvento.horasAtivas !== 'todas') {
      horasAtivas = presetsHorarios[novoEvento.horasAtivas].horas;
    }

    try {
      const response = await fetch(`${API_BASE}/api/eventos`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          nome: audioSelecionado.originalName || spot?.texto?.substring(0, 50) || 'Evento',
          dataInicio: novoEvento.dataInicio,
          dataFim: novoEvento.dataFim,
          minutoNaHora: minuto,
          horasAtivas: horasAtivas,
          audioFile: audioSelecionado.audioRef || audioSelecionado.fileName,
          audioSource: audioSelecionado.origem,
          texto: spot?.textoMelhorado || audioSelecionado.originalName || '',
          tipo: novoEvento.tipo,
        })
      });

      const data = await response.json();

      if (response.ok && data.success) {
        if (data.evento) {
          setEventos(prevEventos => [
            data.evento,
            ...prevEventos.filter(evento => evento.id !== data.evento.id)
          ]);
        }
        carregarEventos();
        
        // ✅ Resetar com data atual
        setNovoEvento({
        dataInicio: getDataHoje(),
        dataFim: getDataHoje(),
        minutoNaHora: '18',
        horasAtivas: 'todas',
        horasCustom: [],
        tipo: 'comercial'
        });
        setAudioSelecionado(null);
        setErro('');
        
        toast.success('Evento agendado com sucesso!');
      } else {
        throw new Error(data.error || 'Erro ao criar evento');
      }
    } catch (error) {
      setErro(`Erro ao agendar: ${error.message}`);
    }
  };

  // Remover evento
  const removerEvento = async (id) => {
  const ok = await confirm({ title: 'Remover evento?', message: 'Esta ação não pode ser desfeita.', danger: true, confirmLabel: 'Remover' });
  if (ok) {
    try {
      const response = await fetch(`${API_BASE}/api/eventos/${id}`, {
        method: 'DELETE'
      });
      
      // ✅ CASO 1: Sucesso (200/201)
      if (response.ok) {
        setEventos(prevEventos => {
          const novosEventos = prevEventos.filter(evento => evento.id !== id);
          console.log(`✅ Evento ${id} removido. Restam ${novosEventos.length} eventos`);
          return novosEventos;
        });
        return;
      }
      
      // ✅ CASO 2: 404 - Não encontrado
      if (response.status === 404) {
        console.log(`🗑️ Evento ${id} não encontrado no servidor (já removido/expirado)`);
        
        setEventos(prevEventos => {
          const eventoRemovido = prevEventos.find(e => e.id === id);
          const novosEventos = prevEventos.filter(evento => evento.id !== id);
          
          if (eventoRemovido) {
            console.log(`📤 Auto-removido: "${eventoRemovido.nome}"`);
          }
          
          return novosEventos;
        });
        
        // SEM ALERT - apenas log
        console.log('✨ Lista atualizada (evento já não existia)');
        return;
      }
      
      // ✅ CASO 3: 500 com mensagem "não encontrado"
      if (response.status === 500) {
        const errorData = await response.json().catch(() => ({ error: 'Erro desconhecido' }));
        
        if (errorData.error && errorData.error.includes('não encontrado')) {
          console.log(`🗑️ Evento ${id} não encontrado (erro 500)`);
          
          setEventos(prevEventos => {
            const novosEventos = prevEventos.filter(evento => evento.id !== id);
            console.log(`📤 Auto-removido após erro 500. Restam ${novosEventos.length}`);
            return novosEventos;
          });
          
          console.log('✨ Evento removido automaticamente');
          return;
        }
        
        // Outro erro 500
        console.error('❌ Erro 500:', errorData);
        toast.error(`Erro no servidor: ${errorData.error}`);
        return;
      }
      
      // ✅ CASO 4: Outros erros
      const errorData = await response.json().catch(() => ({ 
        error: `HTTP ${response.status}` 
      }));
      
      console.error('❌ Erro ao remover:', errorData);
      toast.error(`Erro ao remover evento: ${errorData.error}`);
      
    } catch (error) {
      console.error('❌ Erro de rede:', error);
      toast.error(`Erro de conexão: ${error.message}`);
    }
  }
};

  // Testar spot
  const testarSpot = () => {
    if (!audioSelecionado || spotTocando) return;

    const { audioElement, spotElement } = initGlobalAudio();
    
    const audioUrl = audioSelecionado.audioUrl || audioSelecionado.previewUrl || null;
    if (!audioUrl) {
      toast.warning('Prévia indisponível nesta sessão. O evento ainda pode ser agendado.');
      return;
    }

  const eventoTeste = {
    nome: 'Teste de Spot',
    audioUrl
  };

    setSpotTocando(eventoTeste);

    const volumeOriginal = audioElement.volume;
    if (!audioElement.paused) {
      audioElement.volume = 0.1;
    }

    const urlAbsoluta = eventoTeste.audioUrl.startsWith('http')
   ? eventoTeste.audioUrl
   : `${API_BASE}${eventoTeste.audioUrl}`;
 spotElement.src = encodeURI(urlAbsoluta);
    spotElement.volume = 1.0;

    spotElement.onended = () => {
      setSpotTocando(null);
      if (!audioElement.paused) {
        audioElement.volume = volumeOriginal;
      }
    };

    spotElement.play().catch(err => {
   console.error(err);
   setSpotTocando(null);
   if (!audioElement.paused) audioElement.volume = volumeOriginal;
  });
  };

  return (
    <>
    <div className="page">
    <style>{pulseAnimation}</style>

    <h1 className="page-title">Gerenciar Eventos</h1>

    <div className="page-content">


        <div className="card">
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'space-between',
            marginBottom: secaoAgendarExpandida ? '20px' : '0',
            cursor: 'pointer',
            padding: '10px',
            borderRadius: 0,
            background: 'rgba(var(--brand-rgb), 0.1)',
            transition: 'all 0.2s'
          }}
          onClick={() => setSecaoAgendarExpandida(!secaoAgendarExpandida)}
          onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(var(--brand-rgb), 0.2)'}
          onMouseLeave={(e) => e.currentTarget.style.background = 'rgba(var(--brand-rgb), 0.1)'}
          >
            <h3 style={{ margin: 0, color: 'var(--text-primary)' }}>Agendar Evento por Minuto Fixo</h3>
            <button 
              style={{
                background: 'rgba(var(--brand-rgb), 0.3)',
                border: 'none',
                borderRadius: '6px',
                padding: '8px 16px',
                color: 'white',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: 'bold',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}
              onClick={(e) => {
                e.stopPropagation();
                setSecaoAgendarExpandida(!secaoAgendarExpandida);
              }}
            >
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                <span style={{ display: 'inline-flex', transition: 'transform 0.2s', transform: secaoAgendarExpandida ? 'rotate(180deg)' : 'rotate(0deg)' }}>
                  <ChevronIcon />
                </span>
                {secaoAgendarExpandida ? 'Minimizar' : 'Expandir'}
              </span>
            </button>
          </div>

          {secaoAgendarExpandida && (
            <>
          <AudioSelectorForEvents
            audioGerado={audioGerado}
            onAudioSelected={setAudioSelecionado}
          />

          {audioSelecionado && (
            <div style={{
              marginBottom: '20px',
              padding: '15px',
              background: 'rgba(var(--warning-rgb), 0.1)',
              borderRadius: 0,
              border: '1px solid var(--warning)'
            }}>
              <p style={{ color: 'var(--warning)', fontSize: '14px', marginBottom: '10px' }}>
                Teste do Sistema
              </p>
              <button onClick={testarSpot} className="botao-teste" disabled={spotTocando}>
                {spotTocando ? 'Testando...' : 'Testar Spot Agora'}
              </button>
            </div>
          )}

          <div className="form-evento">
            <div className="linha-form">
              <div>
                <label className="label">Data Inicial</label>
                <input
                  type="date"
                  value={novoEvento.dataInicio}
                  min={getDataHoje()} 
                  onChange={e => setNovoEvento(prev => ({ ...prev, dataInicio: e.target.value }))}
                  className="input-pequeno"
                />
              </div>
              <div>
                <label className="label">Data Final</label>
                <input
                  type="date"
                  value={novoEvento.dataFim}
                  min={novoEvento.dataInicio || getDataHoje()}
                  onChange={e => setNovoEvento(prev => ({ ...prev, dataFim: e.target.value }))}
                  className="input-pequeno"
                />
              </div>
            </div>
            
            {/* ✅ BARRA DE MINUTOS COM MARCADORES */}
            <div style={{ marginTop: '20px' }}>
              <label className="label" style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                Minuto fixo de cada hora
                <span style={{ fontSize: '12px', opacity: 0.85, color: 'var(--text-secondary)' }}>
                  (O evento tocará sempre neste minuto)
                </span>
              </label>
              <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                {/* Container com marcadores */}
                <div style={{ flex: 1, position: 'relative', paddingTop: '20px' }}>
                  {/* Marcadores dos eventos existentes */}
                  {Object.keys(minutosComEventos).map(minuto => {
                    const info = minutosComEventos[minuto];
                    const posicao = (parseInt(minuto) / 59) * 100;
                    const temAtivos = info.ativos > 0;
                    
                    return (
                      <div
                        key={minuto}
                        title={`:${String(minuto).padStart(2, '0')} - ${info.ativos + info.inativos} evento(s)\n${info.nomes.join(', ')}`}
                        style={{
                          position: 'absolute',
                          left: `${posicao}%`,
                          top: '-5px',
                          width: '10px',
                          height: '10px',
                          borderRadius: '50%',
                          background: temAtivos 
                            ? 'var(--gradient-success)' 
                            : 'rgba(var(--error-rgb), 0.8)',
                          border: '2px solid white',
                          boxShadow: 'none',
                          transform: 'translateX(-50%)',
                          cursor: 'pointer',
                          zIndex: 10,
                          transition: 'all 0.2s'
                        }}
                        onMouseEnter={(e) => {
                          e.target.style.transform = 'translateX(-50%) scale(1.5)';
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.transform = 'translateX(-50%) scale(1)';
                        }}
                      >
                        {/* Badge com contagem */}
                        {(info.ativos + info.inativos) > 1 && (
                          <div style={{
                            position: 'absolute',
                            top: '-8px',
                            right: '-8px',
                            background: 'var(--warning)',
                            color: 'white',
                            fontSize: '9px',
                            fontWeight: 'bold',
                            borderRadius: '50%',
                            width: '14px',
                            height: '14px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            border: '1px solid white'
                          }}>
                            {info.ativos + info.inativos}
                          </div>
                        )}
                      </div>
                    );
                  })}
                  
                  {/* Input range */}
                  <input
                    type="range"
                    min="0"
                    max="59"
                    value={novoEvento.minutoNaHora}
                    onChange={e => setNovoEvento(prev => ({ ...prev, minutoNaHora: e.target.value }))}
                    style={{ 
                      width: '100%',
                      cursor: 'pointer'
                    }}
                  />
                </div>
                
                {/* Display do minuto selecionado */}
                <div style={{
                  background: 'var(--gradient-brand)',
                  color: 'white',
                  padding: '10px 20px',
                  borderRadius: 0,
                  fontSize: '24px',
                  fontWeight: 'bold',
                  minWidth: '80px',
                  textAlign: 'center',
                  boxShadow: 'none'
                }}>
                  :{String(novoEvento.minutoNaHora).padStart(2, '0')}
                </div>
              </div>
              
              {/* Legenda e info */}
              <div style={{ fontSize: '12px', opacity: 0.85, color: 'var(--text-secondary)', marginTop: '8px' }}>
                Exemplos de execução: 00:{String(novoEvento.minutoNaHora).padStart(2, '0')}, 
                01:{String(novoEvento.minutoNaHora).padStart(2, '0')}, 
                02:{String(novoEvento.minutoNaHora).padStart(2, '0')}, ...
              </div>
              
              {/* Legenda dos marcadores */}
              {Object.keys(minutosComEventos).length > 0 && (
                <div style={{
                  marginTop: '10px',
                  padding: '10px',
                  background: 'rgba(0,0,0,0.2)',
                  borderRadius: 0,
                  fontSize: '11px',
                  display: 'flex',
                  gap: '15px',
                  alignItems: 'center',
                  color: 'var(--text-secondary)',
                  opacity: 0.9
                }}>
                  <span style={{ fontWeight: 'bold' }}>Legenda:</span>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                    <div style={{ width: '10px', height: '10px', borderRadius: '50%', background: 'var(--gradient-success)' }}></div>
                    <span>Evento ativo</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                    <div style={{ width: '10px', height: '10px', borderRadius: '50%', background: 'rgba(var(--error-rgb), 0.8)' }}></div>
                    <span>Evento inativo</span>
                  </div>
                  <span style={{ opacity: 0.9 }}>• Passe o mouse sobre os marcadores para ver detalhes</span>
                </div>
              )}
            </div>

            <div style={{ marginTop: '20px' }}>
              <label className="label">Quando o evento deve tocar?</label>
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
                gap: '10px',
                marginBottom: '15px'
              }}>
                {Object.entries(presetsHorarios).map(([key, preset]) => (
                  <button
                    key={key}
                    onClick={() => setNovoEvento(prev => ({ ...prev, horasAtivas: key }))}
                    style={{
                      padding: '12px',
                      background: novoEvento.horasAtivas === key
                        ? 'var(--action)'
                        : 'rgba(var(--brand-rgb), 0.1)',
                      border: novoEvento.horasAtivas === key
                        ? '2px solid var(--brand-400)'
                        : '1px solid rgba(var(--brand-rgb), 0.3)',
                      borderRadius: '8px',
                      color: 'white',
                      cursor: 'pointer',
                      fontSize: '13px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      transition: 'all 0.2s'
                    }}
                  >
                    <div style={{ textAlign: 'left', flex: 1 }}>
                      <div style={{ fontWeight: 'bold', fontSize: '12px' }}>{preset.nome}</div>
                      {preset.horas && (
                        <div style={{ fontSize: '10px', opacity: 0.8 }}>
                          {preset.horas.length} horas
                        </div>
                      )}
                    </div>
                  </button>
                ))}
                <button
                  onClick={() => setNovoEvento(prev => ({ ...prev, horasAtivas: 'custom' }))}
                  style={{
                    padding: '12px',
                    background: novoEvento.horasAtivas === 'custom'
                      ? 'linear-gradient(135deg, var(--warning), #92600a)'
                      : 'rgba(var(--warning-rgb), 0.1)',
                    border: novoEvento.horasAtivas === 'custom'
                      ? '2px solid var(--warning)'
                      : '1px solid rgba(var(--warning-rgb), 0.3)',
                    borderRadius: '8px',
                    color: 'white',
                    cursor: 'pointer',
                    fontSize: '13px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}
                >
                  <div style={{ textAlign: 'left', flex: 1 }}>
                    <div style={{ fontWeight: 'bold', fontSize: '12px' }}>Personalizar</div>
                    <div style={{ fontSize: '10px', opacity: 0.8 }}>
                      Escolher horas
                    </div>
                  </div>
                </button>
              </div>

              {novoEvento.horasAtivas === 'custom' && (
                <div style={{
                  padding: '15px',
                  background: 'rgba(var(--warning-rgb), 0.1)',
                  borderRadius: 0,
                  border: '1px solid rgba(var(--warning-rgb), 0.3)'
                }}>
                  <div style={{ fontSize: '13px', marginBottom: '10px', fontWeight: 'bold' }}>
                    Selecione as horas (clique para ativar/desativar):
                  </div>
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(12, 1fr)',
                    gap: '5px'
                  }}>
                    {Array.from({ length: 24 }, (_, i) => (
                      <button
                        key={i}
                        onClick={() => toggleHoraCustom(i)}
                        style={{
                          padding: '6px',
                          background: novoEvento.horasCustom.includes(i)
                            ? 'var(--gradient-success)'
                            : 'rgba(var(--neutral-300-rgb), 0.8)',
                          border: 'none',
                          borderRadius: '6px',
                          color: 'white',
                          cursor: 'pointer',
                          fontSize: '11px',
                          fontWeight: novoEvento.horasCustom.includes(i) ? 'bold' : 'normal'
                        }}
                      >
                        {String(i).padStart(2, '0')}h
                      </button>
                    ))}
                  </div>
                  <div style={{ fontSize: '11px', marginTop: '10px', opacity: 0.7 }}>
                    {novoEvento.horasCustom.length} hora(s) selecionada(s)
                  </div>
                </div>
              )}
            </div>

            {erro && <div className="erro">{erro}</div>}

            <button
              onClick={adicionarEvento}
              className="btn"
              disabled={!novoEvento.dataInicio || !novoEvento.dataFim || !audioSelecionado}
              style={{ 
                marginTop: '20px',
                opacity: (!novoEvento.dataInicio || !novoEvento.dataFim || !audioSelecionado) ? 0.5 : 1
              }}
            >
              Agendar Evento
            </button>
          </div>
            </>
          )}
        </div>

        <div className="secao">
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'space-between',
            marginBottom: secaoEventosExpandida ? '20px' : '0',
            cursor: 'pointer',
            padding: '10px',
            borderRadius: 0,
            background: 'rgba(var(--brand-rgb), 0.1)',
            transition: 'all 0.2s'
          }}
          onClick={() => setSecaoEventosExpandida(!secaoEventosExpandida)}
          onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(var(--brand-rgb), 0.2)'}
          onMouseLeave={(e) => e.currentTarget.style.background = 'rgba(var(--brand-rgb), 0.1)'}
          >
            <h3 style={{ margin: 0, color: 'var(--text-primary)' }}>Eventos Agendados ({eventos.length})</h3>
            <button 
              style={{
                background: 'rgba(var(--brand-rgb), 0.3)',
                border: 'none',
                borderRadius: '6px',
                padding: '8px 16px',
                color: 'white',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: 'bold',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}
              onClick={(e) => {
                e.stopPropagation();
                setSecaoEventosExpandida(!secaoEventosExpandida);
              }}
            >
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                <span style={{ display: 'inline-flex', transition: 'transform 0.2s', transform: secaoEventosExpandida ? 'rotate(180deg)' : 'rotate(0deg)' }}>
                  <ChevronIcon />
                </span>
                {secaoEventosExpandida ? 'Minimizar' : 'Expandir'}
              </span>
            </button>
          </div>

          {secaoEventosExpandida && (
          <div className="lista-eventos">
            {eventos.length === 0 ? (
              <p className="sem-dados">Nenhum evento agendado ainda</p>
            ) : (
              eventos.map(evento => (
                <div key={evento.id} className="card-evento">
                  <div className="evento-header">
                    <strong>{evento.nome}</strong>
                    <button onClick={() => removerEvento(evento.id)} className="botao-remover">
                      Remover
                    </button>
                  </div>
                  <div className="evento-detalhes">
                    <p>
                       <strong>Período:</strong>{' '}
                       {formatarDataExibicao(evento.dataInicio)} até{' '}
                       {formatarDataExibicao(evento.dataFim)}
                    </p>
                    <p>
                      <strong>Execução:</strong> Sempre às :{String(evento.minutoNaHora).padStart(2, '0')} de cada hora
                    </p>
                    {evento.horasAtivas && (
                      <p>
                        <strong>Horas ativas:</strong> {evento.horasAtivas.join('h, ')}h
                      </p>
                    )}
                    <p>
                      <strong>Status:</strong> {evento.ativo ? 'Ativo' : 'Inativo'}
                    </p>

                  </div>
                  {evento.texto && (
                    <div className="evento-spot">
                      <strong>Spot:</strong>
                      <p>"{evento.texto}"</p>
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
          )}
        </div>

      </div>
    </div>
      <ConfirmDialogPortal />
      <ToastContainer />
    </>
  );
}

export default EventosScreen;
